import { CookieResponse } from '@/components/cookie-dialog';
import {
    EnumEventName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
    SuperProperties,
    UserProperties,
} from '@calm/evexia-analytics';
import { useCookieResponse } from '@rhg/hooks/use-cookie-response';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useContext, useMemo } from 'react';
import { useAnalyticsBaseContext } from '../base-context-provider';
import { AnalyticsContext } from '../contexts/analytics-context';
import { AnalyticsStateOverrideContext } from '../contexts/analytics-state-override-context';
import { useAnalyticsEventProps } from '../event-props-provider';
import mapBaseEventProps from '../map-base-event-props';
import { trackAsPageNav as ogTrackAsPageNav } from '../track-page-nav';
import { TrackExtendedTypes, TrackFn, TrackOptions } from '../types';
import { useGatedDispatcher } from '../use-gated-dispatcher';

export const useTracking = () => {
    const contextValue = useContext(AnalyticsContext);
    const { stateOverride } = useContext(AnalyticsStateOverrideContext);
    const { baseProperties, isReady, isIdentityReady } =
        useAnalyticsBaseContext();
    const { response } = useCookieResponse();
    const ambientEventProps = useAnalyticsEventProps();

    // Extract values or use defaults before conditional return
    const destinations = useMemo(
        () => contextValue?.destinations ?? [],
        [contextValue?.destinations]
    );
    const state = contextValue?.state;
    const setCurrentFeature = useMemo(
        () => contextValue?.setCurrentFeature ?? (() => {}),
        [contextValue?.setCurrentFeature]
    );
    const setCurrentScreenName = useMemo(
        () => contextValue?.setCurrentScreenName ?? (() => {}),
        [contextValue?.setCurrentScreenName]
    );

    // Inner dispatch functions gated on concierge permission resolution
    const trackDispatch = useCallback(
        (event: string, properties: TrackExtendedTypes | undefined) =>
            Promise.all(
                destinations.map((dest) =>
                    dest
                        .track(
                            event,
                            mapBaseEventProps({
                                ...baseProperties,
                                ...properties,
                            })
                        )
                        .then(() => {
                            if (event === EnumEventName.Logout) {
                                dest.reset();
                            }
                        })
                )
            )
                .catch(() => {})
                .then(() => {}),
        [destinations, baseProperties]
    );
    const gatedTrackDispatch = useGatedDispatcher(trackDispatch, isReady);

    /**
     * Merges ambient event props (from `AnalyticsEventPropsProvider`) into
     * the explicit per-call properties. Explicit props win on conflict so
     * call sites can override inherited context when needed; pass
     * `{ clearAmbient: true }` to skip the merge entirely.
     *
     * Strips `undefined`-valued keys from the explicit properties before
     * merging so a caller's `{ PackItemName: guide?.title }` doesn't
     * silently clobber the ambient value when `guide` happens to be
     * undefined. Callers wanting to actively unset an ambient key should
     * pass `clearAmbient: true` instead.
     */
    const mergeAmbient = useCallback(
        (
            properties: TrackExtendedTypes | undefined,
            options?: TrackOptions
        ): TrackExtendedTypes | undefined => {
            const definedProps = properties
                ? (Object.fromEntries(
                      Object.entries(properties).filter(
                          ([, v]) => v !== undefined
                      )
                  ) as TrackExtendedTypes)
                : properties;
            if (
                options?.clearAmbient ||
                Object.keys(ambientEventProps).length === 0
            ) {
                return definedProps;
            }
            return { ...ambientEventProps, ...definedProps };
        },
        [ambientEventProps]
    );

    const pageNavDispatch = useCallback(
        (args: {
            screenName: EnumScreenName;
            feature: EnumFeature;
            query?: ParsedUrlQuery;
            additionalProps?: EventProperties;
        }) => {
            ogTrackAsPageNav({
                ...args,
                destinations,
                baseProperties,
            }).catch(() => {});
        },
        [destinations, baseProperties]
    );
    const gatedPageNavDispatch = useGatedDispatcher(pageNavDispatch, isReady);

    const setUserProfileDispatch = useCallback(
        (props: UserProperties) => {
            return Promise.all(
                destinations.map((dest) => dest.setUserProperties(props))
            ).catch(() => {});
        },
        [destinations]
    );
    const gatedSetUserProfile = useGatedDispatcher(
        setUserProfileDispatch,
        isIdentityReady
    );

    const registerDispatch = useCallback(
        (props: SuperProperties) => {
            return Promise.all(
                destinations.map((dest) => dest.register(props))
            ).catch(() => {});
        },
        [destinations]
    );
    /* Account sync awaits register and the circle needs the account, so waiting on isReady would deadlock. */
    const gatedRegister = useGatedDispatcher(registerDispatch, isIdentityReady);

    /**
     * Tracks a page navigation event and **mutates the global analytics screen name state**.
     *
     * Because this updates `currentScreenName` in the shared `AnalyticsContext`, any component
     * that calls this (e.g. when a dialog opens) MUST restore the screen name on every exit
     * path. Use `DialogScreenOverrideProvider` and its `trackDialogClose()` to handle this
     * automatically, or call `restoreScreenName()` manually.
     */
    const trackAsPageNav = useCallback(
        (
            screenName: EnumScreenName,
            feature: EnumFeature,
            query?: ParsedUrlQuery,
            additionalProps?: EventProperties
        ) => {
            if (!contextValue || response === CookieResponse.Essential) return;

            setCurrentFeature(feature);
            setCurrentScreenName(screenName);

            gatedPageNavDispatch({
                screenName,
                feature,
                query,
                additionalProps,
            }).catch(() => {});
        },
        [
            contextValue,
            setCurrentFeature,
            setCurrentScreenName,
            response,
            gatedPageNavDispatch,
        ]
    );

    /**
     * Fires a `Screen : Viewed` page-nav event WITHOUT mutating the global `currentScreenName`/`currentFeature`.
     * Use for subtree-scoped screens (e.g. the media player) where the underlying page screen must remain intact;
     * pair with `AnalyticsStateOverrideProvider` for ambient button screen/feature.
     */
    const trackScreenView = useCallback(
        (
            screenName: EnumScreenName,
            feature: EnumFeature,
            query?: ParsedUrlQuery,
            additionalProps?: EventProperties
        ) => {
            if (!contextValue || response === CookieResponse.Essential) return;

            gatedPageNavDispatch({
                screenName,
                feature,
                query,
                additionalProps,
            }).catch(() => {});
        },
        [contextValue, response, gatedPageNavDispatch]
    );

    const track = useCallback<TrackFn>(
        (event, properties, options) => {
            if (!contextValue || response === CookieResponse.Essential)
                return Promise.resolve();
            return gatedTrackDispatch(event, mergeAmbient(properties, options));
        },
        [contextValue, response, gatedTrackDispatch, mergeAmbient]
    );

    const flush = useCallback(async () => {
        if (!contextValue || response === CookieResponse.Essential) return;

        try {
            await Promise.all(destinations.map((dest) => dest.flush()));
        } catch {
            return;
        }
    }, [contextValue, destinations, response]);

    const restoreScreenName = useCallback(
        (screenName: EnumScreenName, feature: EnumFeature) => {
            if (!contextValue) return;
            contextValue.setCurrentScreenName(screenName);
            contextValue.setCurrentFeature(feature);
        },
        [contextValue]
    );

    // Handle SSR case where context might not be available yet
    if (!contextValue) {
        return {
            trackAsPageNav,
            trackScreenView,
            track,
            flush,
            identify: async () => {},
            setUserProfile: async () => {},
            register: async () => {},
            restoreScreenName,
            state: {
                dialog: {
                    onDialogOpen: () => {},
                    onDialogClose: () => {},
                },
                wizard: {
                    setStep: () => {},
                },
                currentFeature: EnumFeature.NotMapped,
                currentScreenName: EnumScreenName.NotMapped,
            },
        };
    }

    return {
        trackAsPageNav,
        trackScreenView,
        track,
        flush,
        restoreScreenName,
        identify: async (uuid: string) => {
            if (uuid && response !== CookieResponse.Essential) {
                await Promise.all(
                    destinations.map((dest) => dest.identify(uuid))
                );
            }
        },
        setUserProfile: (props: UserProperties) => {
            if (props && response !== CookieResponse.Essential) {
                return gatedSetUserProfile(props).catch(() => {});
            }
            return Promise.resolve();
        },
        /**
         * Registers a super property that will be sent with each event. (stored in the cookie)
         */
        register: (props: SuperProperties) => {
            if (props && response !== CookieResponse.Essential) {
                return gatedRegister(props).catch(() => {});
            }
            return Promise.resolve();
        },
        state: { ...state, ...stateOverride },
    };
};
